import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import _ from "lodash";

import { selectProfile } from "@/redux/selectors/authSelectors";
import { DashboardCard2 } from "@/ui2/molecules/DashboardCard2";

import AccessingPHIDisclaimer from "../../../../components/Common/AccessingPHIDisclaimer";
import DeleteNote from "../../Providers/PatientProfileView/DeleteNote";
import GenericNoteView from "../../../../components/Common/Note/GenericNoteView";
import NoteActions from "../../../../components/Common/Note/NoteActions";
import images from "../../../../utils/images";
import { MemberHeader } from "../../../../component-library/MemberHeader";
import { api } from "../../../../APIRequests";
import { calculateAge } from "../../../../utils/momentUtils";
import { formatMobileNumber } from "../../../../utils/filters";
import { usePatient, usePatientDiagnosis } from "../../../../utils/hooks/usePatient";

import { NoteAddendum } from "./NoteAddendum";

import "./styles.scss";
import { Main2 } from "@/ui2/atoms/Main";

export function ViewFullNote() {
  const { patientId, noteId } = useParams();
  const profile = useSelector(selectProfile);
  const history = useHistory();
  const patient = usePatient(patientId, history.location.state?.patientDetails);
  const diagnosis = usePatientDiagnosis(patientId, history.location.state?.patientDiagnosis);
  const primaryDiagnosis = _.get(diagnosis, "ICD_10_axis_1_diagnosis", [])
    .map((icd_10) => icd_10["value"])
    .join(", ");

  const secondaryDiagnosis = _.get(diagnosis, "ICD_10_axis_2_diagnosis", [])
    .map((icd_10) => icd_10["value"])
    .join(", ");

  const [note, setNote] = useState(null);
  const [isEditable, setIsEditable] = useState(false);
  const [showDeleteNotePopUp, setShowDeleteNotePopUp] = useState(false);
  const [draftNoteId, setDraftNoteId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [addendums, setAddendums] = useState([]);

  const getNote = async () => {
    try {
      let urlParams = { patientId };
      const queryParams = { note_id: noteId };
      let note = await api.shared.fetch_patient_progress_notes({ urlParams, queryParams });
      setNote(note);

      const data = {
        note_id: noteId,
        patient_id: patientId,
      };
      const addendumsResponse = await api.shared.fetch_note_addendums({ urlParams, data });
      setAddendums(addendumsResponse.addendums);
    } catch (e) {
      console.log(e);
    }
  };

  const isNoteEditable = (note) => {
    if (!note) {
      return false;
    }

    return note.note_state === "draft" && profile?.username === note.created_by;
  };

  const deleteNote = async (patientId, noteId) => {
    let data = { id: noteId };
    let urlParams = { patientId: patientId };
    await api.provider.delete_progress_note({ data, urlParams });
    setShowDeleteNotePopUp(false);
    goBack();
  };

  const goBack = () => {
    history.push(`/app/patient/${patientId}/profile/v2?selectedTab=Notes`);
  };

  // This exists because Initial Clinical Assessment notes do not have a draft state
  // they are a old note type that are created while filling the note out and are not
  // deemed completed until they are signed.
  const shouldHideNoteAddendumForIncompleteInitialClinicalAssessment = () => {
    return note?.note_type === "Initial Clinical Assessment" && !note?.signature;
  };

  useEffect(() => {
    getNote();
  }, []);

  useEffect(() => {
    setIsEditable(isNoteEditable(note));
  }, [note, profile]);
  return (
    <Main2>
      <span className="survey-back-link tw:mt-5" onClick={goBack}>
        <img className="img-fluid" src={images("./common/solidBckBtn.svg")} alt={"back"} />
        <p className="d-inline ml-2">Back to Patient Profile</p>
      </span>

      {showDeleteNotePopUp && (
        <DeleteNote
          clickHandler={() => deleteNote(patientId, draftNoteId)}
          close={() => {
            setShowDeleteNotePopUp(false);
          }}
        />
      )}

      <DashboardCard2
        className="tw:!p-5"
        classNameChildren="tw:flex tw:flex-row tw:gap-6 tw:justify-between"
      >
        <AccessingPHIDisclaimer />

        <NoteActions
          isEditable={isEditable}
          isEditing={isEditing}
          note={note}
          patientId={patientId}
          setDraftNoteId={setDraftNoteId}
          setIsEditing={setIsEditing}
          setShowDeleteNotePopUp={setShowDeleteNotePopUp}
        />
      </DashboardCard2>

      {patient && diagnosis && (
        <MemberHeader
          memberName={`${_.get(patient, "first_name", "")} ${_.get(patient, "last_name", "")}`}
          dob={_.get(patient, "dob")}
          age={calculateAge(_.get(patient, "dob", ""))}
          phoneNumber={formatMobileNumber(_.get(patient, "mobile"))}
          dayOfCare={_.get(patient, "number_of_days_in_care", "")}
          primaryDiagnosis={primaryDiagnosis === "" ? "None" : primaryDiagnosis}
          secondaryDiagnosis={secondaryDiagnosis === "" ? "None" : secondaryDiagnosis}
        />
      )}

      <GenericNoteView
        patient={patient}
        patientName={patient && `${patient.first_name} ${patient.last_name}`}
        note={note}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        refetchNote={getNote}
      />

      {note?.note_state !== "draft" &&
        addendums.map((addendum, index) => (
          <NoteAddendum
            addendum={addendum.addendum}
            className="row d-flex flex-wrap justify-content-between ml-auto mr-auto"
            credentials={addendum.credentials}
            key={addendum.id || index}
            noteId={note?.id}
            patientId={patientId}
            signature={addendum.signature}
            textTestId={`note-addendum-text-${index}`}
            timestamp={addendum.timestamp}
          />
        ))}

      {note?.created_by === profile?.username &&
        note?.note_state !== "draft" &&
        note?.note_type !== "Advanced Care Activity" &&
        addendums.length < 5 &&
        !shouldHideNoteAddendumForIncompleteInitialClinicalAssessment() && (
          <NoteAddendum
            className="row d-flex flex-wrap justify-content-between ml-auto mr-auto"
            noteId={note?.id}
            patientId={patientId}
            signature={note?.signature}
            credentials={note?.provider_cp_credentials}
            onSubmit={(addendum) => setAddendums([...addendums, addendum])}
          />
        )}
    </Main2>
  );
}
